import { cn } from "../../lib/utils";

/**
 * StatusStepper — the shared staged-progress indicator used for any portal
 * lifecycle (broker Submitted → … → Closed, collection-item Provide →
 * Review → Done, etc.). The current stage is the clear focal element; past
 * stages read as complete, future stages as muted. Each stage can expose a
 * "what this means / what's next" tooltip. Generalises the portal-local
 * `StepIndicator` and the bespoke ribbon on the broker deal-status page.
 */

export interface StepperStep {
  key: string;
  label: string;
  /** "what this means / what's next" — shown on hover + keyboard focus. */
  hint?: string;
}

export interface StatusStepperProps {
  steps: StepperStep[];
  /** Index of the current stage. Stages before it render complete. */
  currentIndex: number;
  /** When true the current stage is also complete (terminal/done). */
  complete?: boolean;
  /** aria-label for the list. */
  label?: string;
  className?: string;
  "data-testid"?: string;
}

export function StatusStepper({
  steps,
  currentIndex,
  complete = false,
  label = "Progress",
  className,
  "data-testid": testId,
}: StatusStepperProps) {
  return (
    <ol
      className={cn(
        "flex flex-wrap items-center gap-space-2 list-none p-0 m-0",
        className,
      )}
      aria-label={label}
      data-testid={testId}
    >
      {steps.map((step, idx) => {
        const isComplete = idx < currentIndex || (idx === currentIndex && complete);
        const isActive = idx === currentIndex && !complete;
        return (
          <li key={step.key} className="flex items-center gap-space-2 shrink-0">
            <span
              className={cn(
                "inline-flex items-center gap-space-1 rounded-pill border px-space-2 py-[3px] text-caption transition-colors duration-fast ease-standard",
                isActive &&
                  "border-gold bg-gold text-onyx font-semibold ring-1 ring-gold",
                isComplete && "border-status-success bg-status-success/15 text-status-success",
                !isActive && !isComplete && "border-border-light bg-white-warm text-gray-mid",
              )}
              aria-current={isActive ? "step" : undefined}
              title={step.hint}
              tabIndex={step.hint ? 0 : undefined}
              data-step={step.key}
              data-state={isActive ? "active" : isComplete ? "complete" : "upcoming"}
            >
              <span className="inline-block w-4 text-center" aria-hidden="true">
                {isComplete ? "✓" : idx + 1}
              </span>
              {step.label}
            </span>
            {idx < steps.length - 1 && (
              <span className="text-caption text-gray-mid" aria-hidden="true">
                →
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
